<template lang="pug">
.overlay(v-if="display")
    .action-popup
        .header
            v-label.heading Votre session est sur le point d'expirer
            v-label.sub-heading Vous allez être déconnecté(e) dans:
        .container
            v-progress-circular.circluar-progress(:model-value="progressPer" :width="12")
                v-label {{ displayTime }}
        .footer
            v-btn.disconnect-btn(@click="logOut()") Déconnexion
            v-btn.reconnect-btn(@click="resetAndStartWarningListner()") Rester connecté
</template>

<script setup lang="ts">
import { stopPriorityPatientWebWorker } from '~/utils/patients/initiatePriorityPatientWorker'
import InActiveWarningWorker from '~/utils/workers/inactiveWarning.worker?worker'

const { $api: api } = useNuxtApp()
const { signOut } = useAuth()
const display = ref<boolean>(false)
const displayTime = ref<string>('')
const progressPer = ref<number>(100)

const closedIdealSec = ref<number>(0)
const inactivityWarning = ref<number>(4000)
const inactivityLock = ref<number>(6000)
let worker: any
let activeListnerTimer: any

onBeforeUnmount(() => {
  worker?.postMessage(JSON.stringify({ type: 'clear' }))
  worker?.terminate()
  worker = null
})

onUnmounted(() => {
  worker?.postMessage(JSON.stringify({ type: 'clear' }))
  worker?.terminate()
  worker = null
})

onMounted(async () => {
  try {
    const result = await api.cms.getConfig()
    inactivityWarning.value = result.data?.inactivity_warning || 4000
    inactivityLock.value = result.data?.inactivity_lock || 6000
  } catch (error) {
    console.error(error)
  }
  await startWarningListner()
})

const resetAndStartWarningListner = async () => {
  worker?.postMessage(JSON.stringify({ type: 'clear' }))
  localStorage.setItem('closedAt', new Date().toString())
  closedIdealSec.value = 0
  progressPer.value = 100
  await startWarningListner()
}

const startWarningListner = async () => {
  if (localStorage.getItem('closedAt')) {
    const idealSec = Math.abs(new Date(localStorage.getItem('closedAt') as string).getTime() - new Date().getTime()) / 1000
    closedIdealSec.value = idealSec
    inactivityWarning.value = inactivityWarning.value
    inactivityLock.value = inactivityLock.value
  } else {
    closedIdealSec.value = 0
  }
  display.value = false
  if (worker) {
    worker?.terminate()
    worker = null
  }
  worker = new InActiveWarningWorker()
  if (closedIdealSec.value >= inactivityLock.value) {
    await logOut()
    return
  }
  let messageType = 'warning'
  if (closedIdealSec.value >= inactivityWarning.value) {
    display.value = true
    messageType = 'lock'
  }
  const message = {
    type: messageType,
    inactivityWarning: inactivityWarning.value,
    inactivityLock: inactivityLock.value,
    closedIdealSec: closedIdealSec.value,
  }
  worker.postMessage(JSON.stringify(message))

  worker.onmessage = async (event: any) => {
    const data = JSON.parse(event.data)
    switch (data.type) {
      case 'open':
        display.value = true
        const message = {
          type: 'lock',
          inactivityWarning: inactivityWarning.value,
          inactivityLock: inactivityLock.value,
          closedIdealSec: closedIdealSec.value,
        }
        worker.postMessage(JSON.stringify(message))
        break
      case 'logout':
        localStorage.removeItem('closedAt')
        localStorage.setItem('loggedOutAt', new Date().toString())
        progressPer.value = 100
        await logOut()
        break
      case 'timer':
        displayTime.value = data.displayTime
        progressPer.value = data.progressPer
        break
      case 'reset':
        const idealSec =
          Math.abs(new Date(localStorage.getItem('closedAt') as string).getTime() - new Date().getTime()) / 1000
        if (idealSec >= inactivityLock.value) {
          await logOut()
          return
        } else if (idealSec >= inactivityWarning.value) {
          display.value = true
          closedIdealSec.value = idealSec
          worker.postMessage(
            JSON.stringify({
              type: 'lock',
              inactivityWarning: inactivityWarning.value,
              inactivityLock: inactivityLock.value,
              closedIdealSec: closedIdealSec.value,
            }),
          )
          return
        }

        localStorage.setItem('closedAt', new Date().toString())
        closedIdealSec.value = 0
        break
      case 'isActive':
        if (!navigator?.userActivation?.isActive && !localStorage.getItem('closedAt')) {
          localStorage.setItem('closedAt', Date.now().toString())
        }

        const closedAt = localStorage.getItem('closedAt')
        if (closedAt) {
          const closedTime = parseInt(closedAt)
          closedIdealSec.value = !isNaN(closedTime)
            ? (Date.now() - closedTime) / 1000
            : (Date.now() - new Date(closedAt).getTime()) / 1000
        }

        worker.postMessage(
          JSON.stringify({
            type: 'isActive',
            value: navigator?.userActivation?.isActive,
            inactivityWarning: inactivityWarning.value,
            inactivityLock: inactivityLock.value,
            closedIdealSec: closedIdealSec.value,
          }),
        )
        break
      case 'error':
        console.error(data, 'inactive lock')
      default:
        break
    }
  }
}

const logOut = async () => {
  if (activeListnerTimer) {
    clearInterval(activeListnerTimer)
    activeListnerTimer = null
  }
  try {
    await api.auth.deleteSession()
  } catch (error) {
    console.error(error, 'deleteSession')
  }
  await signOut()
  display.value = false
  stopPriorityPatientWebWorker()
  stopDeviceActivityWebWorker()
  window.location.reload()
  const keyToKeep = 'rememberUser'
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i)
    if (typeof key === 'string' && key !== keyToKeep) {
      localStorage.removeItem(key)
    }
  }
}
</script>

<style lang="stylus" scoped>
.overlay
    position absolute
    width 100vw
    height 100vh
    background var(--white-4a, #FFFFFF4A)
    z-index 10000
    backdrop-filter blur(10px)
    display flex
    align-items center
    justify-content center
    .action-popup
        width 50%
        height 50%
        background-color var(--white, #FFFFFF)
        box-shadow 0px 0px 27px -7px var(--outer-space-1, #454545)
        border-radius 20px
        display flex
        flex-direction column
        justify-content center
        align-items center
        .header
            padding 30px 0
            display flex
            flex-direction column
            align-items center
            justify-content center
            gap 10px
            .heading
                opacity 1
                font-size 1.5rem
                white-space pre-wrap
                text-align center
            .sub-heading
                font-size 1.2rem
                white-space pre-wrap
                text-align center
        .container
            flex 1
            display flex
            :deep().v-progress-circular
                height 150px !important
                width 150px !important
                &__overlay
                    stroke-linecap round !important
                    stroke var(--japanese-indigo, #194355)
        .footer
            padding 0 0 10% 0
            display flex
            justify-content space-around
            width 100%
            .disconnect-btn
                font-weight 600
                background-color var(--anti-flash-white, #f0f0f0)
                color var(--japanese-indigo, #194355)
            .reconnect-btn
                font-weight 600
                background-color var(--japanese-indigo, #194355)
                color var(--mint-green, #9BFF90)
</style>
